import { guardNumber } from "@msuite/utils/numbers";
import type { Format } from "@number-flow/react";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import React from "react";
import { Pie, PieChart as RechartsPieChart, ResponsiveContainer, Tooltip } from "recharts";
import { AnimatedNumber } from "../../animated";
import { ChartWrapper, type ChartWrapperProps } from "../chart-wrapper";
import { TooltipContent } from "../components/tooltip-content";
import { CHART_PADDING, CHART_RADIUS } from "../constants";
import type { ChartData, ChartDataProps, DataKey } from "../types";

/** Props Interface */
interface PieChartProps<T extends ChartData>
	extends Omit<ChartWrapperProps, "children" | "isLoading">,
		ChartDataProps<T>,
		VariantProps<typeof pieChartNumber> {
	dataKeys: [DataKey<T>];
	displayKeyMap: Record<DataKey<T>, string>;
	formatter?: (item: number | undefined) => React.ReactNode;
	format?: Format;
	withTotal?: boolean;
	suffix?: string | null;
}

export function PieChart<T extends ChartData>({
	data,
	dataKeys,
	labelSize = "2xl",
	displayKeyMap,
	formatter,
	withTotal = true,
	format = {
		notation: "standard",
		style: "decimal",
		minimumFractionDigits: 2,
	},
	suffix = "€",
}: PieChartProps<T>) {
	/** CVA */
	const animatedNumberClasses = pieChartNumber({ labelSize });

	/** Calculate total */
	const total = data?.reduce((acc, dataItem) => {
		let _acc = acc;

		for (const key of dataKeys) {
			const val = dataItem[key];
			if (!val) continue;
			if (Number.isNaN(val)) continue;
			_acc += val as number;
		}

		return _acc;
	}, 0);

	/** Render */
	return (
		<ChartWrapper isLoading={!data}>
			<div className="relative w-full h-full">
				{withTotal && (
					<div className="bottom-[7.5%] z-0 absolute inset-0 flex flex-col justify-center items-center">
						<AnimatedNumber
							className={clsx("font-bold", animatedNumberClasses)}
							value={guardNumber(total)}
							suffix={suffix || undefined}
							format={format}
						/>
						<p className="label">Total</p>
					</div>
				)}
				<ResponsiveContainer
					width="100%"
					height="100%"
				>
					<RechartsPieChart>
						<Tooltip
							cursor={{
								className: "fill-accent-500/10",
							}}
							content={(props) => {
								return (
									<TooltipContent
										{...props}
										displayKeyMap={displayKeyMap}
										formatter={formatter}
									/>
								);
							}}
							animationDuration={100}
							animationEasing="ease-out"
						/>
						{dataKeys.map((key) => (
							<Pie
								key={key as string}
								data={data}
								dataKey={key as string}
								cx="50%"
								cy="45%"
								startAngle={90}
								endAngle={90 - 360}
								innerRadius="70%"
								outerRadius="90%"
								animationBegin={200}
								paddingAngle={CHART_PADDING}
								cornerRadius={CHART_RADIUS}
								stroke="transparent"
							/>
						))}
					</RechartsPieChart>
				</ResponsiveContainer>
			</div>
		</ChartWrapper>
	);
}

const pieChartNumber = cva([], {
	variants: {
		labelSize: {
			xs: ["text-xs"],
			sm: ["text-sm"],
			base: ["text-base"],
			lg: ["text-lg"],
			xl: ["text-xl"],
			"2xl": ["text-2xl"],
			"3xl": ["text-3xl"],
			"4xl": ["text-4xl"],
		},
	},
});
